import React from "react";
import {
  Sparkles,
  FileText,
  Activity,
  Pill,
  Heart,
  CheckCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Consultation, useConsultationsState } from "@/lib/dashboard/consultationsData";

interface ConsultationSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  consultations: Consultation[];
  isDarkMode: boolean;
}

interface AISummary {
  vitals: {
    name: string;
    value: string;
    status: "normal" | "elevated" | "low";
  }[];
  actions: { category: string; items: string[] }[];
  overallAssessment: string;
}

// Enhanced AI Summarization function using real consultation data
const generateAISummary = (consultations: Consultation[]): AISummary => {
  // Handle edge cases
  if (!consultations || consultations.length === 0) {
    return {
      vitals: [],
      actions: [],
      overallAssessment: "No consultations selected for summary.",
    };
  }

  const vitals: {
    name: string;
    value: string;
    status: "normal" | "elevated" | "low";
  }[] = [];
  const actionMap: { [key: string]: string[] } = {};

  // Enhanced vitals extraction with better error handling
  const extractVitalsFromActions = (actions: any[]) => {
    const extractedVitals: any[] = [];
    
    actions?.forEach((action) => {
      if (action.actionName === "RecordVitals" && action.payload) {
        try {
          const payload = typeof action.payload === 'string' 
            ? JSON.parse(action.payload) 
            : action.payload;
          
          const vitalsDetails = payload.vitalsDetails;
          if (vitalsDetails) {
            // Enhanced weight extraction with status calculation
            if (vitalsDetails.weight) {
              const weightValue = parseFloat(vitalsDetails.weight.Value);
              extractedVitals.push({
                name: "Weight",
                value: `${vitalsDetails.weight.Value} ${vitalsDetails.weight.Unit}`,
                status: weightValue < 50 ? "low" : weightValue > 120 ? "elevated" : "normal"
              });
            }
            
            // Enhanced heart rate with better range detection
            if (vitalsDetails.heartRate) {
              const hrValue = parseInt(vitalsDetails.heartRate.Value);
              extractedVitals.push({
                name: "Heart Rate",
                value: `${hrValue} ${vitalsDetails.heartRate.Unit}`,
                status: hrValue > 100 ? "elevated" : hrValue < 60 ? "low" : "normal"
              });
            }
            
            // Enhanced blood pressure with clinical ranges
            if (vitalsDetails.bloodPressure) {
              const systolic = parseInt(vitalsDetails.bloodPressure.Systolic.Value);
              const diastolic = parseInt(vitalsDetails.bloodPressure.Diastolic.Value);
              const status = systolic > 140 || diastolic > 90 ? "elevated" : 
                           systolic < 90 || diastolic < 60 ? "low" : "normal";
              extractedVitals.push({
                name: "Blood Pressure",
                value: `${systolic}/${diastolic} mmHg`,
                status
              });
            }
            
            // Enhanced temperature with fever detection
            if (vitalsDetails.temperature) {
              const temp = parseFloat(vitalsDetails.temperature.Value);
              extractedVitals.push({
                name: "Temperature",
                value: `${temp} ${vitalsDetails.temperature.Unit}`,
                status: temp > 37.5 ? "elevated" : temp < 36.0 ? "low" : "normal"
              });
            }
            
            // Enhanced BMI with WHO categories
            if (vitalsDetails.bmi) {
              const bmiValue = parseFloat(vitalsDetails.bmi.Value);
              const status = bmiValue > 30 ? "elevated" : 
                           bmiValue < 18.5 ? "low" : "normal";
              extractedVitals.push({
                name: "BMI",
                value: `${bmiValue} ${vitalsDetails.bmi.Unit}`,
                status
              });
            }
            
            // Enhanced respiratory rate
            if (vitalsDetails.respiratoryRate) {
              const rrValue = parseInt(vitalsDetails.respiratoryRate.Value);
              extractedVitals.push({
                name: "Respiratory Rate",
                value: `${rrValue} ${vitalsDetails.respiratoryRate.Unit}`,
                status: rrValue > 20 ? "elevated" : rrValue < 12 ? "low" : "normal"
              });
            }
            
            // Enhanced circumference measurements
            if (vitalsDetails.circumferenceValue) {
              if (vitalsDetails.circumferenceValue.Waist) {
                extractedVitals.push({
                  name: "Waist Circumference",
                  value: `${vitalsDetails.circumferenceValue.Waist} cm`,
                  status: "normal"
                });
              }
              if (vitalsDetails.circumferenceValue.Hip) {
                extractedVitals.push({
                  name: "Hip Circumference",
                  value: `${vitalsDetails.circumferenceValue.Hip} cm`,
                  status: "normal"
                });
              }
            }
          }
        } catch (error) {
          console.warn("Error parsing vitals payload:", error);
        }
      }
    });
    
    return extractedVitals;
  };

  // Process each consultation
  consultations.forEach((consultation) => {
    if (!consultation || typeof consultation !== "object") return;

    const type = consultation?.type || "General Consultation";
    const summary = consultation?.summary || "";
    const transcription = consultation?.transcription || "";

    // Enhanced discussion points extraction
    if (consultation?.template?.templateSections) {
      consultation?.template.templateSections.forEach((section) => {
        if (section?.sectionTitle && section?.userControls) {
          
          // Extract values from user controls with better filtering
          section?.userControls?.forEach((control) => {
            if (control.value && 
                control.value !== "null" && 
                control.value !== "Not specified" &&
                control.value !== "Not mentioned in the transcript" &&
                control.value !== "Not found in transcript") {
            } else if (control?.label && control?.required) {
            }
          });
        }
      });
    }

    // Enhanced medical information extraction
    const medicalConditions = [
      { keyword: "asthma", point: "Asthma management and inhaler use" },
      { keyword: "diabetes", point: "Diabetes screening and management" },
      { keyword: "hypertension", point: "Blood pressure management" },
      { keyword: "weight", point: "Weight management consultation" },
      { keyword: "travel", point: "Travel health assessment" },
      { keyword: "vaccine", point: "Vaccination consultation" },
      { keyword: "allerg", point: "Allergy assessment" }
    ];

    medicalConditions.forEach(({ keyword, point }) => {
      if (summary.toLowerCase().includes(keyword) || 
          transcription.toLowerCase().includes(keyword) ||
          type.toLowerCase().includes(keyword)) {
      }
    });

    // Enhanced vitals extraction
    const consultationVitals = extractVitalsFromActions(consultation.actions || []);
    consultationVitals.forEach(vital => {
      // Only add if not already present or if this is a more recent measurement
      const existingIndex = vitals.findIndex(v => v.name === vital.name);
      if (existingIndex === -1) {
        vitals.push(vital);
      }
    });

    // Enhanced action categorization
    if (consultation.actions && Array.isArray(consultation.actions)) {
      consultation.actions.forEach((action) => {
        if (!action || typeof action !== "object") return;

        let category = "Other Actions";
        let description = action.actionName || "Unknown action";
        
        // Enhanced categorization with better descriptions
        if (action.actionName?.includes("RecordVitals")) {
          category = "Vitals Recording";
          description = "Comprehensive vital signs assessment";
        } else if (action.actionName?.includes("PrescribeMedication")) {
          category = "Prescriptions";
          try {
            const payload = typeof action.payload === 'string' 
              ? JSON.parse(action.payload) 
              : action.payload;
            if (payload.prescriptionsDetails || payload.prescriptionDetails) {
              const drugs = payload.prescriptionsDetails || [payload.prescriptionDetails?.drugs].flat().filter(Boolean);
              if (drugs && drugs.length > 0) {
                const drugNames = drugs.map((drug: any) => 
                  drug.value || drug.drugName || "Unknown medication"
                ).filter(Boolean);
                description = `Prescribed: ${drugNames.join(", ")}`;
              }
            }
          } catch (error) {
            description = "Medication prescription issued";
          }
        } else if (action.actionName?.includes("Vaccinations")) {
          category = "Vaccinations";
          description = "Vaccine administration and immunization";
        } else if (action.actionName?.includes("SendReferralLetter")) {
          category = "Referrals";
          description = "GP/specialist referral for further evaluation";
        } else if (action.actionName?.includes("BookAppointment")) {
          category = "Appointments";
          description = "Follow-up appointment scheduled";
        }

        if (!actionMap[category]) {
          actionMap[category] = [];
        }

        actionMap[category].push(description);
      });
    }
  });

  // Convert action map to array format and remove duplicates
  const actions = Object.entries(actionMap)
    .map(([category, items]) => ({
      category,
      items: [...new Set(items)],
    }))
    .filter(group => group.items.length > 0); // Remove empty groups

  // Enhanced overall assessment
  const totalConsultations = consultations.length;
  const consultationTypes = [
    ...new Set(consultations.map((c) => c.type || "Unknown").filter(Boolean)),
  ];
  const timespan = getTimespan(consultations);
  
  // Enhanced service type counting
  const serviceCounts = {
    travel: consultations.filter(c => c.type?.includes("Travel")).length,
    diabetes: consultations.filter(c => c.type?.includes("Diabetes")).length,
    weightloss: consultations.filter(c => c.type?.includes("Weightloss")).length,
    general: consultations.filter(c => !c.type?.includes("Travel|Diabetes|Weightloss")).length
  };
  
  let assessment = `Based on ${totalConsultations} consultation${totalConsultations > 1 ? "s" : ""} spanning ${timespan}, `;
  
  const serviceDescriptions = [];
  if (serviceCounts.travel > 0) serviceDescriptions.push(`${serviceCounts.travel} travel health`);
  if (serviceCounts.diabetes > 0) serviceDescriptions.push(`${serviceCounts.diabetes} diabetes management`);
  if (serviceCounts.weightloss > 0) serviceDescriptions.push(`${serviceCounts.weightloss} weight management`);
  if (serviceCounts.general > 0) serviceDescriptions.push(`${serviceCounts.general} general health`);
  
  if (serviceDescriptions.length > 0) {
    assessment += `${serviceDescriptions.join(", ")} consultation${totalConsultations > 1 ? "s" : ""}. `;
  }
  
  // Add health status insights
  if (vitals.some(v => v.status === "elevated")) {
    assessment += "Requires monitoring of elevated parameters. ";
  }
  

  
  assessment += "Patient demonstrates good engagement with preventive healthcare services.";


  return {
    vitals: removeDuplicateVitals(vitals),
    actions,
    overallAssessment: assessment,
  };
};

// Enhanced timespan calculation using createdOn dates
const getTimespan = (consultations: Consultation[]): string => {
  if (consultations.length === 0) return "no consultations";
  if (consultations.length === 1) return "a single session";

  try {
    const dates = consultations
      .map((c) => {
        try {
          // Prefer createdOn date for accuracy
          if (c.createdOn) {
            const date = new Date(c.createdOn);
            return isNaN(date.getTime()) ? null : date;
          }
          
          // Fallback to date field if createdOn not available
          const [day, month, year] = c.date.split("/");
          if (!day || !month || !year) return null;

          const fullYear = year.length === 2 ? `20${year}` : year;
          const date = new Date(parseInt(fullYear), parseInt(month) - 1, parseInt(day));
          return isNaN(date.getTime()) ? null : date;
        } catch {
          return null;
        }
      })
      .filter((date): date is Date => date !== null)
      .sort((a, b) => a.getTime() - b.getTime());

    if (dates.length < 2) return "a single session";

    const earliest = dates[0];
    const latest = dates[dates.length - 1];
    const diffTime = Math.abs(latest.getTime() - earliest.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "the same day";
    if (diffDays === 1) return "1 day";
    if (diffDays <= 7) return `${diffDays} days`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} week${Math.ceil(diffDays / 7) > 1 ? 's' : ''}`;
    if (diffDays <= 365) return `${Math.ceil(diffDays / 30)} month${Math.ceil(diffDays / 30) > 1 ? 's' : ''}`;
    return `${Math.ceil(diffDays / 365)} year${Math.ceil(diffDays / 365) > 1 ? 's' : ''}`;
  } catch (error) {
    console.warn("Error calculating timespan:", error);
    return "multiple sessions";
  }
};

// Enhanced duplicate vital removal
const removeDuplicateVitals = (
  vitals: {
    name: string;
    value: string;
    status: "normal" | "elevated" | "low";
  }[],
) => {
  const seen = new Set();
  return vitals.filter((vital) => {
    const key = vital.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getActionIcon = (category: string) => {
  switch (category) {
    case "Prescriptions":
      return <Pill className="w-4 h-4 text-blue-600" />;
    case "Vaccinations":
      return <Heart className="w-4 h-4 text-green-600" />;
    case "Referrals":
      return <FileText className="w-4 h-4 text-purple-600" />;
    case "Appointments":
      return <CheckCircle className="w-4 h-4 text-orange-600" />;
    case "Vitals Recording":
      return <Activity className="w-4 h-4 text-red-600" />;
    default:
      return <Activity className="w-4 h-4 text-gray-600" />;
  }
};

const getVitalStatusColor = (status: "normal" | "elevated" | "low") => {
  switch (status) {
    case "normal":
      return "text-green-600 bg-green-50 border-green-200";
    case "elevated":
      return "text-red-600 bg-red-50 border-red-200";
    case "low":
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    default:
      return "text-gray-600 bg-gray-50 border-gray-200";
  }
};

export function ConsultationSummaryModal({
  isOpen,
  onClose,
  consultations,
  isDarkMode,
}: ConsultationSummaryModalProps) {
  const {consultationBulletPoints} = useConsultationsState()

  const summary = React.useMemo(() => {
    if (!consultations || consultations.length === 0) {
      return {
        discussionPoints: [],
        vitals: [],
        actions: [],
        overallAssessment: "No consultations selected for summary.",
      };
    }
    return generateAISummary(consultations);
  }, [consultations]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-4xl max-h-[80vh] overflow-y-auto transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 text-xl transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <Sparkles className="w-5 h-5 text-purple-500" />
            AI Consultation Summary
          </DialogTitle>
          <DialogDescription
            className={`transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            AI-generated summary of {consultations.length} selected consultation
            {consultations.length > 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall Assessment */}
          <div
            className={`p-4 rounded-lg border transition-colors duration-300 ${
              isDarkMode
                ? "bg-purple-900/20 border-purple-700"
                : "bg-purple-50 border-purple-200"
            }`}
          >
            <h3
              className={`font-semibold mb-2 flex items-center gap-2 transition-colors duration-300 ${
                isDarkMode ? "text-purple-300" : "text-purple-700"
              }`}
            >
              <FileText className="w-4 h-4" />
              Overall Assessment
            </h3>
            <p
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-purple-200" : "text-purple-800"
              }`}
            >
              {summary.overallAssessment}
            </p>
          </div>

          {/* Discussion Points */}
          {consultationBulletPoints?.keyDiscussion?.length > 0 && (
            <div>
              <h3
                className={`font-semibold mb-3 flex items-center gap-2 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <FileText className="w-4 h-4" />
                Key Discussion Points ({consultationBulletPoints?.keyDiscussion?.length})
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {consultationBulletPoints?.keyDiscussion?.map((point, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 p-3 rounded-lg border transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      {point}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vitals */}
          {summary.vitals.length > 0 && (
            <div>
              <h3
                className={`font-semibold mb-3 flex items-center gap-2 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <Activity className="w-4 h-4" />
                Captured Vitals ({summary.vitals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {summary.vitals.map((vital, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-medium text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {vital.name}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs ${getVitalStatusColor(vital.status)}`}
                      >
                        {vital.status.charAt(0).toUpperCase() + vital.status.slice(1)}
                      </Badge>
                    </div>
                    <div
                      className={`text-lg font-bold mt-1 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      {vital.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Taken */}
          {summary.actions.length > 0 && (
            <div>
              <h3
                className={`font-semibold mb-3 flex items-center gap-2 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                Actions Taken
              </h3>
              <div className="space-y-4">
                {summary.actions.map((actionGroup, index) => (
                  <div key={index}>
                    <h4
                      className={`font-medium mb-2 flex items-center gap-2 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {getActionIcon(actionGroup.category)}
                      {actionGroup.category} ({actionGroup.items.length})
                    </h4>
                    <div className="space-y-1 ml-6">
                      {actionGroup.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className={`text-sm p-2 rounded border transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-gray-200"
                              : "bg-gray-50 border-gray-200 text-gray-700"
                          }`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {consultationBulletPoints?.recommendation?.length > 0 && (
            <div
              className={`p-4 rounded-lg border transition-colors duration-300 ${
                isDarkMode
                  ? "bg-green-900/20 border-green-700"
                  : "bg-green-50 border-green-200"
              }`}
            >
              <h3
                className={`font-semibold mb-2 flex items-center gap-2 transition-colors duration-300 ${
                  isDarkMode ? "text-green-300" : "text-green-700"
                }`}
              >
                <Heart className="w-4 h-4" />
                AI Recommendations ({consultationBulletPoints?.recommendation?.length})
              </h3>
              <ul className="space-y-1">
                {consultationBulletPoints?.recommendation?.map((rec, index) => (
                  <li
                    key={index}
                    className={`text-sm flex items-start gap-2 transition-colors duration-300 ${
                      isDarkMode ? "text-green-200" : "text-green-800"
                    }`}
                  >
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Empty State Handling */}
          {
           summary.vitals.length === 0 && 
           summary.actions.length === 0 && (
            <div
              className={`p-8 text-center rounded-lg border transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className={`transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}>
                No data available for selected consultations.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={onClose}
            variant="outline"
            className={`transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : ""
            }`}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
